import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
} from '@mui/material';
import { apiClient } from '../../lib/apiClient';
import { parseFirestoreDate } from '../../lib/dateUtils';

function Transactions({ user }) {
  const [church, setChurch] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filtering, setFiltering] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const myChurches = await apiClient.getMyChurches(user.id);
        if (myChurches.length === 0) {
          setError('You are not assigned to any church');
          return;
        }

        const currentChurch = myChurches[0];
        setChurch(currentChurch);

        // Fetch transactions without date filter initially
        await fetchTransactions(currentChurch.id);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchTransactions = async (churchId, date = null) => {
    try {
      setFiltering(true);
      const records = await apiClient.getPayments(churchId, date || null);
      setTransactions(records);
    } catch (err) {
      setError(err.message);
    } finally {
      setFiltering(false);
    }
  };

  const handleFilter = () => {
    if (!church) return;
    fetchTransactions(church.id, filterDate || null);
  };

  const handleClearFilter = () => {
    setFilterDate('');
    if (church) {
      fetchTransactions(church.id, null);
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = parseFirestoreDate(timestamp);
    if (!date || isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
  };

  const calculateTotal = () => {
    return transactions.reduce((sum, transaction) => sum + (transaction.amount || 0), 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !church) {
    return (
      <Box>
        <Alert severity="error">{error || 'Church not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: '1.5rem', sm: '2rem' },
          fontWeight: 600,
          mb: 3,
          color: '#2C2C2C',
        }}
      >
        Transactions
      </Typography>

      {/* Date Filter */}
      <Paper sx={{ p: 3, mb: 3, maxWidth: '800px', mx: 'auto' }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Filter by Date
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Select Date"
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleFilter}
                disabled={filtering}
                fullWidth
                sx={{
                  backgroundColor: '#d4af39',
                  '&:hover': { backgroundColor: '#8B6F47' },
                }}
              >
                {filtering ? <CircularProgress size={20} /> : 'Filter'}
              </Button>
              {filterDate && (
                <Button
                  variant="outlined"
                  onClick={handleClearFilter}
                  sx={{
                    borderColor: '#d4af39',
                    color: '#d4af39',
                    '&:hover': { borderColor: '#8B6F47', backgroundColor: 'rgba(168, 139, 91, 0.04)' },
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Total Summary */}
      {transactions.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Total: ₹{calculateTotal().toFixed(2)}
          </Typography>
        </Paper>
      )}

      {/* Transactions Table */}
      {transactions.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No transactions found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  User Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Payment Method
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {transaction.userName || transaction.userId}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                    ₹{transaction.amount?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {transaction.paymentMethod || 'cash'}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {formatDateDisplay(transaction.paymentDate)}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {formatDateTime(transaction.createdAt).split(',')[1]?.trim() || 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default Transactions;

